import React, { useMemo } from "react";
import { useHeatmapData } from "../../hooks/useHeatmapData";
import Lottie from "lottie-react";
import fireAnimation from "../../assets/lottie/fire.json";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format a Date as the YYYYMMDD dateID used in Firestore. */
function toDateID(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}${m}${d}`;
}

/** Add `days` calendar days to `base` and return a new Date (time zeroed). */
function addDays(base: Date, days: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  d.setHours(0, 0, 0, 0);
  return d;
}


interface ActivityHeatmapProps {
  userId: string; // Required: User ID for fetching data
  onClick?: () => void;
}

const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({
  userId,
  onClick,
}) => {
  // State for modal
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const currentYear = new Date().getFullYear();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // ── Display geometry ──────────────────────────────────────────────────────
  // Show Jan 1 → Dec 31 as columns (GitHub-style week columns).
  // Future cells are rendered greyed-out via the isFuture check below.
  const startOfYear = new Date(currentYear, 0, 1);
  const endOfYear = new Date(currentYear, 11, 31);
  endOfYear.setHours(0, 0, 0, 0);

  // Jan 1 day-of-week offset (e.g. Thursday = 4 for 2026)
  const jan1DayOfWeek = startOfYear.getDay();
  const daysInPeriod =
    Math.floor(
      (endOfYear.getTime() - startOfYear.getTime()) /
        (1000 * 60 * 60 * 24),
    ) + 1;
  const weeksInYear = Math.ceil((daysInPeriod + jan1DayOfWeek) / 7);

  // Size variables
  const squareSize = isMobile ? 12 : 14;
  const gap = isMobile ? 3 : 4;
  const labelHeight = isMobile ? 14 : 18;

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // ── Data ──────────────────────────────────────────────────────────────────
  const { intensityMap, loading, error } = useHeatmapData(userId);

  // Scroll to the current week on load (so active days are visible)
  React.useEffect(() => {
    if (!loading && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      // Calculate which week column "today" falls in
      const dayOfYear = Math.floor(
        (today.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24),
      );
      const currentWeekIdx = Math.floor((dayOfYear + jan1DayOfWeek) / 7);
      const columnWidth = squareSize + gap;
      // Scroll so the current week is roughly centered
      const targetScroll = currentWeekIdx * columnWidth - container.clientWidth / 2;
      container.scrollLeft = Math.max(0, targetScroll);
    }
  }, [loading, intensityMap, today, startOfYear, jan1DayOfWeek, squareSize, gap]);

  // ── Stats: active days, streaks ──────────────────────────────────────────
  // Iterate every calendar day from Jan 1 → today and look up by dateID.
  // This is the single source of truth — no grid-index math needed.
  const stats = useMemo(() => {
    const yearStart = new Date(currentYear, 0, 1);
    const dayOfYear =
      Math.floor((today.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    const dailyData: { date: Date; intensity: number }[] = [];
    for (let offset = 0; offset < dayOfYear; offset++) {
      const date = addDays(yearStart, offset);
      const intensity = intensityMap.get(toDateID(date)) ?? 0;
      dailyData.push({ date, intensity });
    }

    let activeDays = 0;
    let longestStreak = 0;
    let tempStreak = 0;

    for (const { intensity } of dailyData) {
      if (intensity > 0) {
        activeDays++;
        tempStreak++;
        longestStreak = Math.max(longestStreak, tempStreak);
      } else {
        tempStreak = 0;
      }
    }

    // Current streak: walk backward from today
    let currentStreak = 0;
    for (let i = dailyData.length - 1; i >= 0; i--) {
      if (dailyData[i].intensity > 0) {
        currentStreak++;
      } else {
        break;
      }
    }

    const percentage = Math.round((activeDays / dayOfYear) * 100);
    return { activeDays, dayOfYear, percentage, currentStreak, longestStreak };
  }, [intensityMap, currentYear, today]);

  // ── Month labels for the week-column header row ───────────────────────────
  const getMonthLabels = useMemo(() => {
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const weeksByMonth: { [key: string]: number[] } = {};

    for (let weekIdx = 0; weekIdx < weeksInYear; weekIdx++) {
      const weekDate = addDays(startOfYear, weekIdx * 7);
      if (weekDate.getFullYear() === currentYear) {
        const key = `${currentYear}-${weekDate.getMonth()}`;
        if (!weeksByMonth[key]) weeksByMonth[key] = [];
        weeksByMonth[key].push(weekIdx);
      }
    }

    return Object.entries(weeksByMonth).map(([key, wks]) => {
      const [, monthIdx] = key.split("-");
      const span = wks.length;
      return {
        centerWeek: wks[Math.floor(span / 2)],
        month: months[parseInt(monthIdx)],
        span,
      };
    });
  }, [currentYear, weeksInYear, startOfYear]);

  // ── Color scale (0-7, theme-aware via CSS variables) ─────────────────────
  const getColor = (intensity: number): string => {
    const level = Math.min(Math.max(intensity, 0), 7);
    return `var(--heatmap-${level})`;
  };

  // ── Pre-compute grid cell data (fixes stale-render with Zustand Maps) ───
  // Matching the pattern used by FullYearModal's yearCalendar useMemo:
  // bake intensity values into a data structure so React tracks the
  // intensityMap dependency and re-renders when data arrives.
  const weekGrid = useMemo(() => {
    return Array.from({ length: weeksInYear }, (_, weekIdx) => {
      return Array.from({ length: 7 }, (_, dayOfWeek) => {
        const dayOffset = weekIdx * 7 + dayOfWeek - jan1DayOfWeek;
        const cellDate = addDays(startOfYear, dayOffset);

        const isBeforeYear = cellDate < startOfYear;
        const isAfterPeriod = cellDate > endOfYear;

        if (isBeforeYear || isAfterPeriod) {
          return { date: null, intensity: 0, isFuture: false };
        }

        const isFuture = cellDate > today;
        const intensity = isFuture
          ? 0
          : (intensityMap.get(toDateID(cellDate)) ?? 0);

        return { date: cellDate, intensity, isFuture };
      });
    });
  }, [intensityMap, weeksInYear, jan1DayOfWeek, startOfYear, endOfYear, today]);

  // Day labels
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="flex" style={{ gap: `${gap}px` }}>
      {Array.from({ length: weeksInYear }).map((_, weekIndex) => (
        <div
          key={weekIndex}
          className="flex flex-col"
          style={{ gap: `${gap}px` }}
        >
          <div style={{ height: `${labelHeight}px` }}></div>
          {Array.from({ length: 7 }).map((_, dayIndex) => (
            <div
              key={`${weekIndex}-${dayIndex}`}
              className="rounded-sm animate-pulse bg-gradient-to-br from-gray-200 via-gray-300 to-gray-200"
              style={{
                width: `${squareSize}px`,
                height: `${squareSize}px`,
                animationDelay: `${(weekIndex + dayIndex) * 20}ms`,
                animationDuration: "1.5s",
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );

  // Error state component
  if (error) {
    return (
      <div className="bg-base-100 rounded-none md:rounded-lg px-8 py-6 mt-3">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-heading">
            Activity <span className="font-grotesk text-base-content/60">Heatmap</span>
          </h3>
        </div>
        <div className="text-center py-8">
          <p className="text-error text-sm mb-2">
            Failed to load activity data
          </p>
          <p className="text-base-content/40 text-xs">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-base-100 rounded-none md:rounded-lg px-8 py-6 mt-3">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-heading">
          Activity <span className="font-grotesk text-base-content/60">Heatmap</span>
          <span className="ml-2 text-xs text-base-content/40 font-normal">
            • {currentYear}
          </span>
        </h3>
        <button
          onClick={() => setIsModalOpen(true)}
          className="text-xs text-primary hover:text-accent font-medium transition-colors duration-200 flex items-center gap-1"
        >
          View Full Year
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <>
          {/* GitHub-style continuous week grid */}
          <div className="relative">
            {/* Day labels column - STICKY */}
            <div
              className="absolute left-0 top-0 z-10 bg-base-100 flex flex-col text-xs text-base-content/40"
              style={{ gap: `${gap}px`, paddingRight: `${gap * 2}px` }}
            >
              <div style={{ height: `${labelHeight}px` }}></div>{" "}
              {/* Spacer for month labels */}
              {days.map((day, index) => (
                <div
                  key={day}
                  style={{
                    height: `${squareSize}px`,
                    lineHeight: `${squareSize}px`,
                  }}
                  className="flex items-center"
                >
                  {index % 2 === 1 ? (
                    <span className={isMobile ? "text-[10px]" : "text-[11px]"}>
                      {isMobile ? day.charAt(0) : day.substring(0, 3)}
                    </span>
                  ) : (
                    ""
                  )}
                </div>
              ))}
            </div>

            {/* Scrollable container */}
            <div
              ref={scrollContainerRef}
              className="overflow-x-auto scrollbar-hide pb-2"
              style={{ paddingLeft: isMobile ? "24px" : "40px" }}
            >
              <div className="flex" style={{ gap: `${gap}px` }}>
                {weekGrid.map((week, weekIdx) => {
                  const monthLabel = getMonthLabels.find(
                    (m) => m.centerWeek === weekIdx,
                  );

                  return (
                    <div
                      key={weekIdx}
                      className="flex flex-col flex-shrink-0"
                      style={{
                        gap: `${gap}px`,
                        minWidth: `${squareSize}px`,
                        width: `${squareSize}px`,
                      }}
                    >
                      {/* Month label row */}
                      <div
                        className="text-xs text-base-content/40 flex items-start justify-center"
                        style={{ height: `${labelHeight}px` }}
                      >
                        {monthLabel && (
                          <span
                            className={
                              isMobile
                                ? "text-[10px] font-medium"
                                : "text-[11px] font-semibold"
                            }
                          >
                            {monthLabel.month}
                          </span>
                        )}
                      </div>

                      {/* 7 day cells — Sun … Sat */}
                      {week.map((cell, dayOfWeek) => {
                        if (!cell.date) {
                          // Transparent placeholder — keeps grid alignment
                          return (
                            <div
                              key={dayOfWeek}
                              className="flex-shrink-0"
                              style={{
                                width: `${squareSize}px`,
                                height: `${squareSize}px`,
                                backgroundColor: "transparent",
                              }}
                            />
                          );
                        }

                        return (
                          <div
                            key={dayOfWeek}
                            className={`rounded transition-all duration-200 flex-shrink-0 ${
                              !isMobile && !cell.isFuture
                                ? "hover:ring-2 hover:ring-primary hover:ring-opacity-50"
                                : ""
                            }`}
                            style={{
                              width: `${squareSize}px`,
                              height: `${squareSize}px`,
                              backgroundColor: getColor(cell.intensity),
                              opacity: cell.isFuture ? 0.3 : 1,
                            }}
                            title={
                              !isMobile
                                ? `${cell.date.toLocaleDateString()} ${
                                    cell.isFuture
                                      ? "(Future)"
                                      : `— Intensity: ${cell.intensity}`
                                  }`
                                : undefined
                            }
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div
            className="flex items-center justify-end mt-4 text-xs text-base-content/40"
            style={{ gap: `${gap * 2}px` }}
          >
            <span className={isMobile ? "text-[11px]" : "text-sm"}>Less</span>
            <div className="flex" style={{ gap: `${gap}px` }}>
              {[0, 1, 2, 3, 4, 5, 6, 7].map((level) => (
                <div
                  key={level}
                  className="rounded"
                  style={{
                    width: `${squareSize}px`,
                    height: `${squareSize}px`,
                    backgroundColor: getColor(level),
                  }}
                />
              ))}
            </div>
            <span className={isMobile ? "text-[11px]" : "text-sm"}>More</span>
          </div>

        </>
      )}

      {/* Full Year Modal */}
      {isModalOpen && (
        <FullYearModal
          userId={userId}
          currentYear={currentYear}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

// Full Year Modal Component
interface FullYearModalProps {
  userId: string;
  currentYear: number;
  onClose: () => void;
}

const FullYearModal: React.FC<FullYearModalProps> = ({
  userId,
  currentYear: initialYear,
  onClose,
}) => {
  const [selectedYear, setSelectedYear] = React.useState(initialYear);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentYear = new Date().getFullYear();

  // Single source of truth — dateID map, no grid translation.
  // Fetches (and caches) per year; switching years triggers a fresh fetch automatically.
  const { intensityMap, loading } = useHeatmapData(userId, selectedYear);

  // Helper: look up intensity for a date
  const getIntensity = (date: Date): number =>
    intensityMap.get(toDateID(date)) ?? 0;

  // Color scale (0-7, theme-aware via CSS variables)
  const getColor = (intensity: number): string => {
    const level = Math.min(Math.max(intensity, 0), 7);
    return `var(--heatmap-${level})`;
  };

  // ── Stats: iterate every calendar day of selectedYear up to today ─────────
  const stats = useMemo(() => {
    const yearStart = new Date(selectedYear, 0, 1);
    const isCurrentYear = selectedYear === currentYear;
    const endDate = isCurrentYear ? today : new Date(selectedYear, 11, 31);
    endDate.setHours(0, 0, 0, 0);

    const totalDays = Math.floor(
      (endDate.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24),
    ) + 1;

    let activeDays = 0;
    let longestStreak = 0;
    let tempStreak = 0;
    let currentStreak = 0;

    const dailyIntensities: number[] = [];
    for (let offset = 0; offset < totalDays; offset++) {
      dailyIntensities.push(getIntensity(addDays(yearStart, offset)));
    }

    for (const intensity of dailyIntensities) {
      if (intensity > 0) {
        activeDays++;
        tempStreak++;
        longestStreak = Math.max(longestStreak, tempStreak);
      } else {
        tempStreak = 0;
      }
    }

    if (isCurrentYear) {
      for (let i = dailyIntensities.length - 1; i >= 0; i--) {
        if (dailyIntensities[i] > 0) currentStreak++;
        else break;
      }
    }

    const percentage = Math.round((activeDays / totalDays) * 100);
    return { activeDays, totalDays, percentage, longestStreak, currentStreak };
  }, [intensityMap, selectedYear, currentYear, today]);

  // ── Calendar grid: one object per month, weeks × 7 cells ─────────────────
  const yearCalendar = useMemo(() => {
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

    return months.map((monthName, monthIndex) => {
      const firstDay = new Date(selectedYear, monthIndex, 1);
      const lastDay = new Date(selectedYear, monthIndex + 1, 0);
      const startDayOfWeek = firstDay.getDay(); // 0 = Sun

      const weeks: Array<
        Array<{ date: Date | null; intensity: number; isFuture: boolean }>
      > = [];
      let currentWeek: Array<{
        date: Date | null;
        intensity: number;
        isFuture: boolean;
      }> = [];

      // Leading empty cells
      for (let i = 0; i < startDayOfWeek; i++) {
        currentWeek.push({ date: null, intensity: 0, isFuture: false });
      }

      // Actual days — intensity from map by dateID
      for (let day = 1; day <= lastDay.getDate(); day++) {
        const date = new Date(selectedYear, monthIndex, day);
        date.setHours(0, 0, 0, 0);
        const isFuture = date > today;
        const intensity = isFuture ? 0 : getIntensity(date);

        currentWeek.push({ date, intensity, isFuture });

        if (currentWeek.length === 7) {
          weeks.push(currentWeek);
          currentWeek = [];
        }
      }

      // Trailing empty cells to complete the last row
      if (currentWeek.length > 0) {
        while (currentWeek.length < 7) {
          currentWeek.push({ date: null, intensity: 0, isFuture: false });
        }
        weeks.push(currentWeek);
      }

      return { name: monthName, monthIndex, weeks };
    });
  }, [intensityMap, selectedYear, today]);

  // Prevent body scroll while modal is open
  React.useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = "unset"; };
  }, []);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-base-100 rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-base-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-heading">
                Activity{" "}
                <span className="font-grotesk text-base-content/60">Heatmap</span>
              </h3>

              {/* Year Navigator */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setSelectedYear((prev) => Math.max(currentYear - 1, prev - 1))
                  }
                  disabled={selectedYear <= currentYear - 1}
                  className="p-1 hover:bg-base-200 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Previous year"
                >
                  <svg
                    className="w-4 h-4 text-base-content/60"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                <span className="text-xs text-base-content/40 font-medium min-w-[3rem] text-center">
                  {selectedYear}
                </span>

                <button
                  onClick={() =>
                    setSelectedYear((prev) => Math.min(currentYear, prev + 1))
                  }
                  disabled={selectedYear >= currentYear}
                  className="p-1 hover:bg-base-200 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Next year"
                >
                  <svg
                    className="w-4 h-4 text-base-content/60"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-1.5 hover:bg-base-200 rounded-full transition-colors"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5 text-base-content/50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Stats */}
          {!loading && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-base-content/50">
              <span className="font-medium text-base-content">
                {stats.activeDays} of {stats.totalDays} days
              </span>
              <span className="text-base-content/30">•</span>
              <span className="font-medium text-base-content">
                {stats.percentage}%
              </span>

              {/* Current Streak with Fire Animation */}
              {stats.currentStreak > 0 && selectedYear === currentYear && (
                <>
                  <span className="text-base-content/30">•</span>
                  <div className="flex items-center gap-1">
                    <Lottie
                      animationData={fireAnimation}
                      loop={true}
                      autoplay={true}
                      style={{ width: 24, height: 24 }}
                      className="flex-shrink-0"
                    />
                    <span className="font-medium text-primary">
                      {stats.currentStreak}{" "}
                      {stats.currentStreak === 1 ? "day" : "days"} streak
                    </span>
                  </div>
                </>
              )}

              {stats.longestStreak > 0 && (
                <>
                  <span className="text-base-content/30">•</span>
                  <span className="text-base-content/50">
                    {stats.longestStreak}{" "}
                    {stats.longestStreak === 1 ? "day" : "days"} longest streak
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="grid grid-cols-3 gap-x-8 gap-y-8">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-base-300 rounded w-10 mb-2"></div>
                  <div className="space-y-0.5">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <div key={j} className="flex gap-0.5">
                        {Array.from({ length: 7 }).map((_, k) => (
                          <div
                            key={k}
                            className="w-3 h-3 bg-base-300 rounded flex-shrink-0"
                            style={{ aspectRatio: "1 / 1" }}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-x-8 gap-y-8">
              {yearCalendar.map((month) => (
                <div key={month.monthIndex}>
                  <h4 className="text-xs font-medium mb-2 text-base-content/70">
                    {month.name}
                  </h4>
                  <div className="space-y-0.5">
                    {month.weeks.map((week, weekIdx) => (
                      <div key={weekIdx} className="flex gap-0.5">
                        {week.map((day, dayIdx) => (
                          <div
                            key={`${weekIdx}-${dayIdx}`}
                            className="w-3 h-3 rounded transition-all hover:scale-110 flex-shrink-0"
                            style={{
                              backgroundColor: day.date
                                ? day.isFuture
                                  ? 'var(--heatmap-0)'
                                  : getColor(day.intensity)
                                : "transparent",
                              opacity: day.date ? (day.isFuture ? 0.3 : 1) : 0,
                              cursor:
                                day.date && !day.isFuture
                                  ? "pointer"
                                  : "default",
                              aspectRatio: "1 / 1",
                            }}
                            title={
                              day.date
                                ? `${day.date.toLocaleDateString()} - ${day.isFuture ? "Future" : `Activity: ${day.intensity}`}`
                                : ""
                            }
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Longest Streak */}
        {!loading && stats.longestStreak > 0 && (
          <div className="px-6 py-4 border-t border-base-200 bg-base-200">
            <div className="text-xs text-base-content/50 text-center">
              <span className="font-medium text-base-content/70">Longest Streak:</span>{" "}
              <span className="font-semibold text-primary">
                {stats.longestStreak}{" "}
                {stats.longestStreak === 1 ? "day" : "days"}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityHeatmap;
